import * as ko from 'knockout';
import Alert from '../webHostClient/alert';
import panic from '../panic';

import { SelectInputBindable } from './selectInputBindable';
import TextInputBindable from './textInputBindable';
import SubmitButtonBindable from './submitButtonBindable';
import SubmitRequestBody from '../webHostClient/enquiry/submitRequest'
import EnquiryClient from '../webHostClient/enquiry/enquiryClient';
import SelectOption from '../webHostClient/selectOption';
import storage from '../storage';

type EnquiryFormMode = "normal" | "orderdetails" | "compact";

const categories: Array<string> = [
    "ordering products",
    "consultancy services",
    "registration",
    "pricing",
    "my account",
    "delivery",
    "other"];

const orderDetailCategories: Array<string> = [
    "provide feedback for this order",
    "raise an issue relating to this order",
    "request a change to this order",
    "ask for support on understanding this order",
    "other"];


export class EnquiryFormBindable {

    public readonly alerts: ko.ObservableArray<Alert> = ko.observableArray([]);
    public readonly showAlerts: ko.Computed<boolean>;
    public readonly showForm: ko.Computed<boolean>;
    public heading: ko.Observable<string> = ko.observable("Get in touch");
    public introLine1: ko.Observable<string> = ko.observable("Have a project you'd like to discuss in more detail?");
    public introLine2: ko.Observable<string> = ko.observable("Send us a message here and one of our utility experts will be in touch.");

    public readonly category: SelectInputBindable = new SelectInputBindable({
        labelText: '',
        validationErrorText: '',
        isRequired: true
    });

    public readonly message: TextInputBindable = new TextInputBindable({
        labelText: 'Message',
        validationErrorText: 'Message is required',
        isRequired: true,
        lines: 10,
        maxLength: 10000
    });

    public readonly compactMessage: TextInputBindable = new TextInputBindable({
        labelText: 'Message',
        validationErrorText: 'Message is required',
        isRequired: true,
        lines: 10,
        maxLength: 10000
    });

    public readonly showName: ko.Computed<boolean>;
    public readonly name: TextInputBindable = new TextInputBindable({
        labelText: 'Name',
        validationErrorText: 'Name must be provided',
        isRequired: true,
        maxLength: 128
    });

    public readonly compactName: TextInputBindable = new TextInputBindable({
        labelText: 'Name',
        validationErrorText: 'Name must be provided',
        isRequired: true,
        maxLength: 128
    });

    public readonly showEmail: ko.Computed<boolean>;
    public readonly email = new TextInputBindable({
        labelText: 'Email',
        validationErrorText: 'A valid email address must be provided',
        isRequired: true,
        maxLength: 128,
        validationPattern: /^[^@\s]+@[^@\s]+$/
    });

    public readonly compactEmail = new TextInputBindable({
        labelText: 'Email',
        validationErrorText: 'A valid email address must be provided',
        isRequired: true,
        maxLength: 128,
        validationPattern: /^[^@\s]+@[^@\s]+$/
    });

    public readonly showOrganisation: ko.Computed<boolean>;
    public readonly organisation = new TextInputBindable({
        labelText: 'Company name',
        validationErrorText: '',
        isRequired: false,
        maxLength: 128
    });

    public readonly showPhone: ko.Computed<boolean>;
    public readonly phone = new TextInputBindable({
        labelText: 'Phone',
        validationErrorText: 'Phone number is invalid',
        isRequired: true,
        maxLength: 24,
        validationPattern: /^\+?[0-9\s-]+$/
    });

    public readonly acceptTermsAndPolicyClicked: ko.Observable<boolean> = ko.observable(false);

    private reset(): void {
        this.email.loadComplete('');
        this.message.loadComplete('');
        this.name.loadComplete('');
        this.organisation.loadComplete('');
        this.phone.loadComplete('');
        this.compactName.loadComplete('');
        this.compactEmail.loadComplete('');
        this.compactMessage.loadComplete('');
        this.compactPhone.loadComplete('');
    }

    public readonly compactPhone = new TextInputBindable({
        labelText: 'Phone',
        validationErrorText: 'Phone number is invalid',
        isRequired: true,
        maxLength: 24,
        validationPattern: /^\+?[0-9\s-]+$/
    });

    public readonly submit: SubmitButtonBindable;

    public readonly mode: ko.Observable<EnquiryFormMode> = ko.observable<EnquiryFormMode>("normal");

    constructor(mode?: EnquiryFormMode) {
        if ("compact" === mode) {
            this.submit = new SubmitButtonBindable({
                buttonText: 'Submit form',
                loadingText: 'Loading...',
                submit: () => this._doSubmitAsync(),
                computeCanSubmit: () => this._canSubmit()
            });
        } else {
            this.submit = new SubmitButtonBindable({
                buttonText: 'Submit',
                loadingText: 'Loading...',
                submit: () => this._doSubmitAsync(),
                computeCanSubmit: () => this._canSubmit()
            });
        }

        if (mode) {
            this.mode(mode);
        }

        if ("orderdetails" === this.mode()) {
            // TODO : Add to resources
            this.category.labelText("I want to...");
            this.category.loadComplete(0, orderDetailCategories.map<SelectOption>((t, i) => { return { text: t, value: i, isOther: false, isDisabled: false } }));
        }
        else {
            this.category.labelText("I want help with....");
            this.category.loadComplete(0, categories.map<SelectOption>((t, i) => { return { text: t, value: i, isOther: false, isDisabled: false } }));
        }

        this.showAlerts = ko.computed(() => this.alerts().length > 0);
        this.showForm = ko.computed(() => 0 == this.alerts().length);
        this.showEmail = ko.computed(() => "normal" === this.mode());
        this.showName = ko.computed(() => "normal" === this.mode());
        this.showOrganisation = ko.computed(() => "normal" === this.mode());
        this.showPhone = ko.computed(() => "normal" === this.mode());

        this.email.loadComplete('');
        this.message.loadComplete('');
        this.name.loadComplete('');
        this.organisation.loadComplete('');
        this.phone.loadComplete('');
        this.compactName.loadComplete('');
        this.compactEmail.loadComplete('');
        this.compactMessage.loadComplete('');
        this.compactPhone.loadComplete('');
    }

    private async _doSubmitAsync() {

        if ("orderdetails" === this.mode()) {

            const orderToken: string = storage.getInProgressOrderToken();
            const categoryName: string = this.category.options().find(o => o.value === this.category.selected()).text;

            const orderDetailsBody: SubmitRequestBody = {
                category: categoryName,
                message: this.message.value(),
                organisation: null,
                orderToken: orderToken,
                name: null,
                email: null,
                phone: null,
            }

            const enquiryClient: EnquiryClient = new EnquiryClient();

            try {
                await enquiryClient.submitAsync(orderDetailsBody);
            }
            catch
            {
                panic("0f8871ca-6000-41d3-9bde-0759603b0fce");
                return;
            }
        }
        else {
            if ("compact" === this.mode()) {

                const compactBody: SubmitRequestBody = {
                    email: this.compactEmail.value(),
                    message: this.compactMessage.value(),
                    name: this.compactName.value(),
                    phone: this.compactPhone.value(),
                    organisation: null,
                    orderToken: null,
                    category: null
                }
                const enquiryClient: EnquiryClient = new EnquiryClient();

                try {
                    await enquiryClient.submitAsync(compactBody);
                }
                catch
                {
                    panic("7a94245e-3549-483d-ac20-f58ea0da71f8");
                }
            } else {

                let categoryName: string = this.category.options().find(o => o.value === this.category.selected()).text;

                const body: SubmitRequestBody = {
                    category: categoryName,
                    email: this.email.value(),
                    message: this.message.value(),
                    name: this.name.value(),
                    organisation: this.organisation.value(),
                    phone: this.phone.value(),
                    orderToken: null
                }
                const enquiryClient: EnquiryClient = new EnquiryClient();

                try {
                    await enquiryClient.submitAsync(body);
                }
                catch
                {
                    panic("21b92111-efb6-4ebf-b0b3-6b87e07e26a2");
                    return;
                }
            }

            this.alerts.removeAll();
            this.alerts.push({
                level: "success",
                strongText: "Enquiry submitted",
                text: "Thanks for getting in touch, one of our utility experts will aim to respond within 24 hours.",
                textIsHtml: false,
                removeAlert: this.removeAlert(this),
            });

            this.reset();
            this.submit.submitting(false);
        }
    }

    private _canSubmit(): boolean {
        let hasValidationError;

        if ("orderdetails" === this.mode()) {
            hasValidationError = this.category.validationError() || this.message.validationError();
        }
        else if ("compact" === this.mode()) {
            hasValidationError = this.compactEmail.validationError()
                || this.compactMessage.validationError()
                || this.compactName.validationError()
                || this.compactPhone.validationError()
                || this.acceptTermsAndPolicyClicked() == false;
        }
        else {
            hasValidationError = this.category.validationError()
                || this.email.validationError()
                || this.message.validationError()
                || this.name.validationError()
                || this.organisation.validationError()
                || this.phone.validationError()
                || this.acceptTermsAndPolicyClicked() == false;
        }
        return !hasValidationError;
    }

    private removeAlert(page) {
        return (alert) => {
            page.alerts.remove(alert);
        }
    }
}
